import { Formik } from 'formik';
import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import { get_list_of_setting, get_machine_category_with_groups, get_grafana_url, get_warehouse_areas_dropdown } from '@/utils/api';
import useAppSetting from '@/hooks/useAppSetting';
import { get_common_sub_categories_by_machine_group, get_common_sub_categories_by_location } from './mbmr_api';
import {
  buildCascaderLeafValue,
  resolveMachineDetails,
  LockedMachineSetupPanel,
} from './machineRoutingHelpers';
import {
  Form,
  Select,
  Input,
  InputNumber,
  Button,
  Row,
  Col,
  Tooltip,
  Switch,
  Tag,
  Cascader,
  Checkbox,
} from 'antd';
import { Activity, Clock, Gauge, CheckCircle, Edit3, Trash2, Lock, Info } from 'lucide-react';
import { useTheme } from '@/App';

const { TextArea } = Input;

const GRACE_WINDOW_UNITS = [
  { value: 'sec', label: 'Seconds' },
  { value: 'min', label: 'Minutes' },
  { value: 'hrs', label: 'Hours' },
];

// Generic value+unit duration parser — shared by Grace Window and Escalation Delay.
// Also migrates legacy free-text strings (e.g. "2 Min") saved before either field
// was split into structured value/unit inputs.
const parseDuration = (raw, value, unit, fallback) => {
  if (value !== undefined && value !== null && unit) {
    return { value: Number(value), unit };
  }
  const match = String(raw || '').match(/(\d+(?:\.\d+)?)\s*(sec|second|min|minute|hr|hour)s?/i);
  if (!match) return fallback;
  const rawUnit = match[2].toLowerCase();
  return {
    value: Number(match[1]),
    unit: rawUnit.startsWith('sec') ? 'sec' : rawUnit.startsWith('min') ? 'min' : 'hrs',
  };
};

const parseGraceWindow = (graceWindow, value, unit) =>
  parseDuration(graceWindow, value, unit, { value: 1, unit: 'min' });

const parseEscalationDelay = (escalationDelay, value, unit) =>
  parseDuration(escalationDelay, value, unit, { value: 2, unit: 'min' });

const formatGraceWindow = (value, unit) => `± ${value} ${unit}`;
const formatEscalationDelay = (value, unit) => `${value} ${unit}`;

// REMOVED: MachineRegisterSection component
const validationSchema = Yup.object().shape({
  machine_type_id: Yup.mixed().nullable().when('target_type', {
    is: (targetType) => targetType !== 'Location',
    then: (schema) => schema.required('Machine is required'),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  machine_group_id: Yup.mixed().nullable().when('target_type', {
    is: (targetType) => targetType !== 'Location',
    then: (schema) => schema.required('Machine group is required'),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  machine_id: Yup.string().nullable(),
  parameter_id: Yup.string().nullable().when('track_reading', {
    is: true,
    then: (schema) => schema.required('Parameter is required'),
    otherwise: (schema) => schema.notRequired().nullable(),
  }),
  reading_count: Yup.number()
    .nullable()
    .when(['startTime', 'endTime', 'mode', 'reading_schedule_type'], {
      is: (startTime, endTime, mode, scheduleType) =>
        scheduleType === 'Count' && !startTime && !endTime && mode !== 'auto',
      then: (schema) => schema.required('Number of readings required'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  monitoring_frequency: Yup.number().nullable(),
  grace_window_value: Yup.number()
    .nullable()
    .min(0, 'Grace Window cannot be negative')
    .required('Grace Window is required'),
  grace_window_unit: Yup.string().nullable().required('Grace Window unit is required'),
  quantity: Yup.number().nullable(),
  exp_value: Yup.string()
    .nullable()
    .when('parameter_data_type', {
      is: (dataType) => dataType === 'Boolean' || dataType === 'String',
      then: (schema) => schema.required('Expected value is required'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  min_value: Yup.number().nullable(),
  max_value: Yup.number()
    .nullable()
    .when('min_value', {
      is: (val) => val !== null,
      then: (schema) => schema.min(Yup.ref('min_value'), 'Max must be >= Min'),
    }),
  duration_min_value: Yup.number().nullable(),
  duration_max_value: Yup.number()
    .nullable()
    .when('duration_min_value', {
      is: (val) => val !== null,
      then: (schema) => schema.min(Yup.ref('duration_min_value'), 'Max must be >= Min'),
    }),
  instruction: Yup.string().nullable(),
  startTime: Yup.boolean().nullable(),
  endTime: Yup.boolean().nullable(),
  mode: Yup.string().nullable().required('Choose Auto/Manual'),
});

// Deviation rule options — when a reading is treated as a deviation.
const DEVIATION_RULE_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'less_than_min', label: 'Less than Min' },
  { value: 'greater_than_max', label: 'Greater than Max' },
  { value: 'less_than_min_or_greater_than_max', label: 'Less than Min or Greater than Max' },
];

// Capture Mode — multi-select (a monitoring point can be captured by more than one method).
const CAPTURE_MODE_OPTIONS = [
  { value: 'Manual', label: 'Manual' },
  { value: 'Photo/OCR', label: 'Photo/OCR' },
  { value: 'Integration Assisted', label: 'Integration Assisted' },
];

const TARGET_TYPE_OPTIONS = [
  { value: 'Machine', label: 'Machine' },
  { value: 'Location', label: 'Location' },
];

const READING_SCHEDULE_OPTIONS = [
  { value: 'Adhoc', label: 'Adhoc' },
  { value: 'Count', label: 'Count' },
  { value: 'Periodic', label: 'Periodic' },
];

// Execution Mode determines what's physically possible: a fully Automatic feed can't
// be captured by someone typing a value, and it can't run on "operator decides when."
const CAPTURE_MODE_AVAILABILITY = {
  Manual: ['Manual', 'Photo/OCR', 'Integration Assisted'],
  'Semi-Automatic': ['Photo/OCR', 'Integration Assisted'],
  Automatic: ['Integration Assisted'],
};

const READING_SCHEDULE_AVAILABILITY = {
  Manual: ['Adhoc', 'Count', 'Periodic'],
  'Semi-Automatic': ['Adhoc', 'Count', 'Periodic'],
  Automatic: ['Count', 'Periodic'],
};

const DURATION_UNITS = [
  { value: 'sec', label: 'sec' },
  { value: 'min', label: 'min' },
  { value: 'hrs', label: 'hrs' },
];

// Migrates a legacy single-value capture_type ('Manual'|'Automatic') onto the new
// capture_modes multi-select so existing saved rules/items keep working.
const legacyCaptureTypeToModes = (captureType) =>
  captureType === 'Automatic' ? ['Integration Assisted'] : ['Manual'];

const resolveCaptureModes = (lockedRuleFields, values) => {
  if (Array.isArray(lockedRuleFields?.capture_modes) && lockedRuleFields.capture_modes.length) {
    return lockedRuleFields.capture_modes;
  }
  if (Array.isArray(values.capture_modes) && values.capture_modes.length) {
    return values.capture_modes;
  }
  if (lockedRuleFields?.capture_type) return legacyCaptureTypeToModes(lockedRuleFields.capture_type);
  if (values.capture_type) return legacyCaptureTypeToModes(values.capture_type);
  return ['Manual'];
};

// Migrates the old is_continuous / reading_count / monitoring_frequency combo onto an
// explicit reading_schedule_type. "Continuous" isn't a distinct 4th state — it's just
// Periodic with no bounded operation duration in the picture (unchanged behavior below).
const resolveReadingScheduleType = (lockedRuleFields, values) => {
  const existing = lockedRuleFields?.reading_schedule_type || values.reading_schedule_type;
  if (existing) return existing;
  if (values.reading_count) return 'Count';
  const legacyContinuous = values.is_continuous ?? values.is_continues ?? false;
  if (legacyContinuous || values.monitoring_frequency) return 'Periodic';
  return 'Adhoc';
};

const normalizeTriggerType = (triggerType) => {
  if (triggerType === 'Event Based') return 'Event Triggered';
  if (!triggerType || triggerType === 'Time Based') return 'User';
  return triggerType;
};

// Backend stores Boolean-parameter Expected Value as a real BooleanField
// (Process_Parameter.expected_bool_value); some read paths serialize it back
// as a raw JSON boolean rather than the 'True'/'False' strings the Expected
// Value <Select> options use, so `true`/`false` matched neither option and
// rendered blank. Normalize whatever shape comes back to those two strings.
const normalizeExpValue = (v) => {
  if (v === true || v === 'true' || v === 'True') return 'True';
  if (v === false || v === 'false' || v === 'False') return 'False';
  return v ?? null;
};

export default function Monitoring({
  editIndex,
  onSubmit = (values) => console.log('Submitted values:', values),
  operation_duration = 0,
  app_settings: appSettingsProp,
  lockedRuleFields,
  one_machine_rule_enabled = false,
  configured_machines = [],
  machine_setup_entries = [],
  process_id,
  // Areas already selected on the parent Process (ProcessForm.js's `area`/`area_section_id`).
  // Used as the Location target — no separate location picker is built here.
  process_areas = [],
  values = {
    machine_type_id: null,
    machine_type_name: null,
    machine_group_id: null,
    machine_id: null,
    machine_name: null,
    // Default to Location when Machine Setup hasn't been configured yet for this
    // operation — Machine stays selectable in theory, but with no machines set up
    // there's nothing to pick, so start somewhere actually usable.
    target_type:
      one_machine_rule_enabled && (machine_setup_entries || []).length === 0
        ? 'Location'
        : 'Machine',
    location_area: null,
    parameter_id: null,
    parameter: null,
    parameter_unit: null,
    parameter_data_type: null,
    track_reading: true,
    track_duration: false,
    duration_min_value: null,
    duration_max_value: null,
    duration_unit: 'min',
    // Integration Assisted source — a shared external Service (from URL Configuration).
    integration_service: null,
    reading_count: null,
    monitoring_frequency: null,
    quantity: null,
    min_value: null,
    max_value: null,
    deviation_rule: 'none',
    exp_value: null,
    instruction: null,
    startTime: false,
    endTime: false,
    mode: 'Manual',
    is_qr_scan: false,
    is_kitting: false,
    capture_modes: ['Manual'],
    reading_schedule_type: 'Periodic',
    grace_window: '± 1 min',
    grace_window_value: 1,
    grace_window_unit: 'min',
    trigger_type: 'User',
    escalation_delay: '2 min',
    escalation_delay_value: 2,
    escalation_delay_unit: 'min',
    // Editable Runtime Execution Rules (defaults from rule). Saved per item (separate JSON).
    execution_rules: null,
  },
}) {
  const rulesEnabled = useAppSetting('Rules');
  const [appSettings, setAppSettings] = useState(appSettingsProp || []);
  const [machineTypeWithGroups, setMachineTypeWithGroups] = useState([]);
  const [frequencyUnit, setFrequencyUnit] = useState('sec'); // 'sec' or 'min'
  const hasKnownDuration = operation_duration > 0;
  // Auto-apply machine from setup if rule is enabled
  const ruleLockedCategory =
    one_machine_rule_enabled && machine_setup_entries?.[0]?.machine_type_id
      ? machine_setup_entries[0].machine_type_id
      : null;
  const ruleLockedGroup =
    one_machine_rule_enabled && machine_setup_entries?.[0]?.machine_group_id
      ? machine_setup_entries[0].machine_group_id
      : null;
  const ruleLockedLabel = machine_setup_entries?.[0]?.machine_type || '';
  const ruleLockedMachineIds = (() => {
    if (Array.isArray(configured_machines) && configured_machines.length) {
      return configured_machines.map(Number).filter(Boolean);
    }
    const setup = machine_setup_entries?.[0] || {};
    if (Array.isArray(setup.machine_ids) && setup.machine_ids.length) {
      return setup.machine_ids.map(Number).filter(Boolean);
    }
    if (setup.machine_ids_csv) {
      return String(setup.machine_ids_csv).split(',').map(Number).filter(Boolean);
    }
    return setup.machine_id ? [Number(setup.machine_id)].filter(Boolean) : [];
  })();
  const ruleLockedMachineId = ruleLockedMachineIds[0] || null;
  const setupMachineDetails = resolveMachineDetails(
    ruleLockedMachineIds,
    machineTypeWithGroups,
    machine_setup_entries?.[0] || {}
  );
  const [cascaderValue, setCascaderValue] = useState(null);
  const hasRuleValue = (key) =>
    lockedRuleFields?.[key] !== undefined &&
    lockedRuleFields?.[key] !== null &&
    lockedRuleFields?.[key] !== '';
  const isRuleFieldLocked = (key) =>
    hasRuleValue(key) && lockedRuleFields?.[`${key}_editable`] !== true;
  // A rule can hide a field from the recipe/execution config entirely (distinct
  // from locking it, which still shows it read-only) — mirrors Ingredients.js's
  // `${key}_hidden` pattern.
  const isRuleFieldHidden = (key) => lockedRuleFields?.[`${key}_hidden`] === true;
  const isRuntimeRuleLocked = (key) =>
    hasRuleValue(key) && lockedRuleFields?.[`${key}_editable`] !== true;
  const parsedGraceWindow = parseGraceWindow(
    lockedRuleFields?.grace_window || values.grace_window,
    lockedRuleFields?.grace_window_value ?? values.grace_window_value,
    lockedRuleFields?.grace_window_unit || values.grace_window_unit
  );
  const parsedEscalationDelay = parseEscalationDelay(
    lockedRuleFields?.escalation_delay || values.escalation_delay,
    lockedRuleFields?.escalation_delay_value ?? values.escalation_delay_value,
    lockedRuleFields?.escalation_delay_unit || values.escalation_delay_unit
  );

  useEffect(() => {
    if (appSettingsProp?.length) {
      setAppSettings(appSettingsProp);
    } else {
      get_list_of_setting((res) => {
        if (res?.data?.data) setAppSettings(res.data.data);
      });
    }
  }, [appSettingsProp]);

  useEffect(() => {
    get_machine_category_with_groups((res) => {
      setMachineTypeWithGroups(res?.data?.data || []);
    }, process_id);
  }, [process_id]);

  // Configured external services (Configuration → System Integration → URL Configuration)
  // — Service Name options for the Integration Assisted → Service source.
  const [integrationServices, setIntegrationServices] = useState([]);
  useEffect(() => {
    get_grafana_url((res) => {
      const list = (res?.data?.data || []).filter((item) => item?.type);
      setIntegrationServices(list);
    });
  }, []);

  // process_areas only carries area NAMES (see SubOperationDetails.js); resolve
  // the selected name back to a WerehouseAreaConfig id here so Target=Location
  // can look up its own parameters, same as ProcessForm.js does for area_section_id.
  const [warehouseAreas, setWarehouseAreas] = useState([]);
  useEffect(() => {
    get_warehouse_areas_dropdown((res) => {
      setWarehouseAreas(res?.data?.data || []);
    });
  }, []);

  // Restore cascader value on edit / when data loads
  useEffect(() => {
    if (!machineTypeWithGroups.length) return;
    const catId = ruleLockedCategory || values.machine_type_id;
    const grpId = ruleLockedGroup || values.machine_group_id;
    const selectedMachineIds = ruleLockedMachineIds.length
      ? ruleLockedMachineIds
      : Array.isArray(values.machine_ids) && values.machine_ids.length
        ? values.machine_ids.map(Number)
        : values.machine_ids_csv
          ? String(values.machine_ids_csv).split(',').map(Number).filter(Boolean)
          : (values.machine_id ? [Number(values.machine_id)] : []);
    if (catId && grpId) {
      const cat = machineTypeWithGroups.find((c) => String(c.machine_category_id) === String(catId));
      const grp = cat?.machine_groups?.find((g) => String(g.machine_group_id) === String(grpId));
      const groupMachineIds = (grp?.machines || []).map((m) => Number(m.machine_id));
      const leaf = buildCascaderLeafValue(selectedMachineIds, groupMachineIds);
      setCascaderValue([catId, grpId, leaf]);
    } else if (catId) {
      setCascaderValue([catId]);
    } else {
      setCascaderValue(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.machine_type_id,
    values.machine_group_id,
    values.machine_id,
    JSON.stringify(values.machine_ids),
    values.machine_ids_csv,
    JSON.stringify(ruleLockedMachineIds),
    machineTypeWithGroups.length,
  ]);

  // Parameters loaded for the selected machine type
  const [machineParameters, setMachineParameters] = useState([]);
  const [machineParamsLoading, setMachineParamsLoading] = useState(false);

  const loadParametersForMachineGroup = (machine_group_id) => {
    if (!machine_group_id) {
      setMachineParameters([]);
      return;
    }
    setMachineParamsLoading(true);
    get_common_sub_categories_by_machine_group(
      (res) => {
        setMachineParamsLoading(false);
        if (res?.data?.data) setMachineParameters(res.data.data);
        else setMachineParameters([]);
      },
      { machine_group_id }
    );
  };

  const loadParametersForLocation = (location_id) => {
    if (!location_id) {
      setMachineParameters([]);
      return;
    }
    setMachineParamsLoading(true);
    get_common_sub_categories_by_location(
      (res) => {
        setMachineParamsLoading(false);
        if (res?.data?.data) setMachineParameters(res.data.data);
        else setMachineParameters([]);
      },
      { location_id }
    );
  };

  useEffect(() => {
    if (values.target_type === 'Location') {
      const areaId = warehouseAreas.find((a) => a.name === values.location_area)?.id || null;
      loadParametersForLocation(areaId);
      return;
    }
    // Load parameters when the group is known — either from prop values or from rule-locked setup
    const groupId = values.machine_group_id || ruleLockedGroup;
    if (groupId) loadParametersForMachineGroup(groupId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.target_type, values.location_area, values.machine_group_id, ruleLockedGroup, warehouseAreas]);

  const initialValues = {
    ...values,
    machine_type_id: ruleLockedCategory || values.machine_type_id || null,
    machine_type_name: ruleLockedCategory ? ruleLockedLabel : values.machine_type_name || null,
    machine_group_id: ruleLockedGroup || values.machine_group_id || null,
    machine_id: ruleLockedMachineId || values.machine_id || null,
    machine_name: values.machine_name || null,
    machine_ids: ruleLockedMachineIds.length
      ? ruleLockedMachineIds
      : Array.isArray(values.machine_ids) && values.machine_ids.length
        ? values.machine_ids
        : values.machine_ids_csv
          ? String(values.machine_ids_csv).split(',').map(Number).filter(Boolean)
          : (values.machine_id ? [values.machine_id] : []),
    machine_ids_csv: ruleLockedMachineIds.length
      ? ruleLockedMachineIds.join(',')
      : values.machine_ids_csv || (
        Array.isArray(values.machine_ids) && values.machine_ids.length
          ? values.machine_ids.join(',')
          : (values.machine_id ? String(values.machine_id) : '')
      ),
    parameter: values.parameter || null,
    parameter_unit: values.parameter_unit || null,
    exp_value: normalizeExpValue(values.exp_value),
    // Rule can hide Quantity entirely for operation types that don't need it —
    // still needs a real default (0) so the field isn't just left blank/undefined.
    quantity: lockedRuleFields?.quantity ?? values.quantity ?? 0,
    _data_type_list: values.parameter_data_type ? [values.parameter_data_type] : [],
    // Derive frequency from reading_count when API doesn't return monitoring_frequency
    monitoring_frequency: values.monitoring_frequency ??
      (values.reading_count && operation_duration > 0
        ? Math.round(operation_duration / values.reading_count)
        : null),
    is_qr_scan: values.is_qr_scan ?? false,
    is_kitting: values.is_kitting ?? false,
    mode: lockedRuleFields?.mode || values.mode || 'Manual',
    // Capture Mode — multi-select; migrates legacy single-value capture_type.
    capture_modes: resolveCaptureModes(lockedRuleFields, values),
    // Target — Machine (default, existing Cascader) or Location (the process's own area).
    target_type: lockedRuleFields?.target_type || values.target_type || 'Machine',
    location_area: values.location_area || null,
    // Reading and Duration are independent — both may be tracked on one monitoring point.
    track_reading:
      lockedRuleFields?.track_reading ?? values.track_reading ?? true,
    track_duration:
      lockedRuleFields?.track_duration ?? values.track_duration ?? false,
    duration_min_value: values.duration_min_value ?? null,
    duration_max_value: values.duration_max_value ?? null,
    duration_unit: values.duration_unit || 'min',
    integration_service: values.integration_service || null,
    // Reading Schedule — migrates the legacy is_continuous/reading_count/monitoring_frequency combo.
    reading_schedule_type: resolveReadingScheduleType(lockedRuleFields, values),
    grace_window:
      lockedRuleFields?.grace_window ||
      values.grace_window ||
      formatGraceWindow(parsedGraceWindow.value, parsedGraceWindow.unit),
    grace_window_value: parsedGraceWindow.value,
    grace_window_unit: parsedGraceWindow.unit,
    trigger_type: normalizeTriggerType(lockedRuleFields?.trigger_type || values.trigger_type),
    escalation_delay:
      lockedRuleFields?.escalation_delay ||
      values.escalation_delay ||
      formatEscalationDelay(parsedEscalationDelay.value, parsedEscalationDelay.unit),
    escalation_delay_value: parsedEscalationDelay.value,
    escalation_delay_unit: parsedEscalationDelay.unit,
    // Deviation rule: prefill the rule default; editable unless the rule locks it.
    deviation_rule:
      lockedRuleFields?.deviation_rule || values.deviation_rule || 'none',
    instruction: lockedRuleFields?.instruction ?? values.instruction ?? null,
  };

  const ALLOWED_RULES = ['Photo Capture Required'];

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(vals, { resetForm }) => {
        // Resolve Runtime Execution Rules (separate JSON): rule default per key,
        // overridden by any per-item choice (vals.execution_rules).
        const _rDefaults = {
          allow_dynamic_grouping: true,
          auto_include_in_execution_round: true,
          critical_parameter: true,
          allow_partial_submission: false,
        };
        const _rBool = (key, fallback) =>
          lockedRuleFields && lockedRuleFields[key] !== undefined && lockedRuleFields[key] !== null
            ? !!lockedRuleFields[key]
            : fallback;
        const resolvedExecutionRules = Object.keys(_rDefaults).reduce((acc, key) => {
          const chosen = (vals.execution_rules || {})[key];
          acc[key] = chosen !== undefined && chosen !== null ? !!chosen : _rBool(key, _rDefaults[key]);
          return acc;
        }, {});
        // eslint-disable-next-line no-console
        console.log('[Monitoring submit] vals', vals);
        // Execution Mode caps which Capture Modes and Reading Schedules are valid —
        // defensive clamp in case stale data was carried over from a different mode.
        const executionMode = vals.mode || 'Manual';
        const allowedCaptureModes = CAPTURE_MODE_AVAILABILITY[executionMode] || CAPTURE_MODE_AVAILABILITY.Manual;
        const clampedCaptureModes = (Array.isArray(vals.capture_modes) ? vals.capture_modes : [])
          .filter((m) => allowedCaptureModes.includes(m));
        const captureModes = clampedCaptureModes.length ? clampedCaptureModes : [allowedCaptureModes[0]];
        const allowedSchedules = READING_SCHEDULE_AVAILABILITY[executionMode] || READING_SCHEDULE_AVAILABILITY.Manual;
        // Reading Schedule caps which of count/frequency/trigger are meaningful —
        // clear the ones that don't apply to the chosen schedule.
        const scheduleType = allowedSchedules.includes(vals.reading_schedule_type)
          ? vals.reading_schedule_type
          : 'Periodic';
        // Grace Window and Escalation Delay always travel together: Adhoc never has
        // them; Count only has them when the operation has a known duration (needed
        // to derive an interval); Periodic always has them regardless of duration.
        const graceEscalationApplies =
          scheduleType === 'Periodic' || (scheduleType === 'Count' && hasKnownDuration);
        // Grace window also models human timing tolerance — only meaningful when
        // Manual is an active capture mode. No Manual capture ⇒ no grace window.
        const isMachineTarget = vals.target_type !== 'Location';
        const isIntegrationAssisted = captureModes.includes('Integration Assisted');
        const graceValue = (graceEscalationApplies && captureModes.includes('Manual'))
          ? vals.grace_window_value
          : 0;
        const machineIds = isMachineTarget && Array.isArray(vals.machine_ids) && vals.machine_ids.length
          ? vals.machine_ids
          : (isMachineTarget && vals.machine_id ? [vals.machine_id] : []);
        onSubmit({
          ...vals,
          capture_modes: captureModes,
          grace_window_value: graceValue,
          grace_window: formatGraceWindow(graceValue, vals.grace_window_unit),
          escalation_delay_value: graceEscalationApplies ? vals.escalation_delay_value : null,
          escalation_delay_unit: graceEscalationApplies ? vals.escalation_delay_unit : null,
          escalation_delay: graceEscalationApplies
            ? formatEscalationDelay(vals.escalation_delay_value, vals.escalation_delay_unit)
            : null,
          frequency: vals.monitoring_frequency ?? 0,
          reading_schedule_type: scheduleType,
          reading_count: scheduleType === 'Count' ? vals.reading_count : null,
          monitoring_frequency: scheduleType === 'Periodic' ? vals.monitoring_frequency : null,
          trigger_type: scheduleType === 'Adhoc' ? normalizeTriggerType(vals.trigger_type) : null,
          // Always send the selected deviation rule; default to 'none'.
          deviation_rule: vals.deviation_rule || 'none',
          execution_rules: resolvedExecutionRules,
          machine_ids: machineIds,
          machine_ids_csv: machineIds.join(','),
          // One shared Service applies regardless of which of reading/duration is
          // tracked; cleared entirely when Integration Assisted isn't active.
          integration_service: isIntegrationAssisted ? (vals.integration_service || null) : null,
          location_area: !isMachineTarget ? vals.location_area : null,
        });
        resetForm();
      }}
      enableReinitialize
    >
      {({ values, errors, touched, setFieldValue, handleSubmit }) => {
        const filteredSettings = appSettings?.filter(
          (s) => s?.data_type === 'boolean' && ALLOWED_RULES.includes(s?.module)
        );

        // When editing an existing item, parameter_data_type may not come from the API.
        // Derive it from machineParameters so the Lower/Upper Limit row is visible.
        const effectiveDataType = values.parameter_data_type || (() => {
          if (!values.parameter_id || !machineParameters?.length) return null;
          const matched = machineParameters.find(
            (p) => String(p.id) === String(values.parameter_id)
          );
          if (!matched) return null;
          const dt = matched.data_type;
          return Array.isArray(dt) ? dt[0] : dt || null;
        })();

        const clearParam = () => {
          setFieldValue('parameter_id', null);
          setFieldValue('parameter', null);
          setFieldValue('parameter_unit', null);
          setFieldValue('parameter_data_type', null);
          setFieldValue('quantity', null);
          setFieldValue('min_value', null);
          setFieldValue('max_value', null);
          setFieldValue('exp_value', null);
        };

        // Force-sync the locked machine fields into Formik's live state whenever the
        // One Machine Per Operation rule resolves them — `initialValues` alone can miss
        // this if machine_setup_entries loads after the form has already mounted/reinitialized.
        /* eslint-disable react-hooks/rules-of-hooks */
        useEffect(() => {
          if (!one_machine_rule_enabled || values.target_type === 'Location') return;
          if (!ruleLockedCategory || !ruleLockedGroup) return;
          if (
            values.machine_type_id === ruleLockedCategory &&
            values.machine_group_id === ruleLockedGroup
          ) {
            return;
          }
          setFieldValue('machine_type_id', ruleLockedCategory);
          setFieldValue('machine_group_id', ruleLockedGroup);
          setFieldValue('machine_id', ruleLockedMachineId);
          setFieldValue('machine_ids', ruleLockedMachineIds);
          setFieldValue('machine_ids_csv', ruleLockedMachineIds.join(','));
          loadParametersForMachineGroup(ruleLockedGroup);
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [
          one_machine_rule_enabled,
          ruleLockedCategory,
          ruleLockedGroup,
          values.target_type,
          values.machine_type_id,
          values.machine_group_id,
        ]);
        /* eslint-enable react-hooks/rules-of-hooks */

        // ── Rule-driven values ──
        const ruleVal = (key, fallback) =>
          lockedRuleFields && lockedRuleFields[key] !== undefined && lockedRuleFields[key] !== null
            ? lockedRuleFields[key]
            : fallback;
        const verification = ruleVal('verification', 'Supervisor Required');
        const executionModeLabel = ruleVal('execution_mode_label', 'Dynamic Rounds');
        const runtimeRules = [
          { key: 'allow_dynamic_grouping', label: 'Allow Dynamic Grouping' },
          { key: 'auto_include_in_execution_round', label: 'Auto Include In Execution Round' },
          { key: 'critical_parameter', label: 'Critical Parameter (CPP)' },
          { key: 'allow_partial_submission', label: 'Allow Partial Submission' },
        ].filter(({ key }) => lockedRuleFields?.[`${key}_hidden`] !== true);
        const runtimeDefaults = {
          allow_dynamic_grouping: true,
          auto_include_in_execution_round: true,
          critical_parameter: true,
          allow_partial_submission: false,
        };
        const sectionHeader = (text) => (
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">{text}</p>
        );
        // Grace Window and Escalation Delay always travel together:
        //   Adhoc              → neither applies (always 0/none)
        //   Count, no duration → same as Adhoc (can't derive an interval without a duration)
        //   Count, has duration → both apply
        //   Periodic           → both apply always, regardless of duration
        const graceEscalationApply =
          values.reading_schedule_type === 'Periodic' ||
          (values.reading_schedule_type === 'Count' && hasKnownDuration);
        const escalationDelayField = isRuleFieldHidden('escalation_delay') ? null : (
          <Form.Item
            label={
              <span className="flex items-center gap-1">
                Escalation Delay
                <Tooltip title="Counted from when the reading becomes overdue — i.e. after the grace window has expired, not from the scheduled time itself.">
                  <Info className="w-3 h-3 text-gray-400 cursor-help" />
                </Tooltip>
                {isRuleFieldLocked('escalation_delay') && (
                  <Lock className="w-3 h-3 text-yellow-600" />
                )}
              </span>
            }
            style={{ marginBottom: 0 }}
          >
            <div className="flex gap-2">
              <InputNumber
                className="flex-1"
                min={0}
                step="any"
                disabled={isRuleFieldLocked('escalation_delay')}
                value={values.escalation_delay_value}
                onChange={(val) => setFieldValue('escalation_delay_value', val)}
                placeholder="Amount"
              />
              <Select
                style={{ width: 130 }}
                disabled={isRuleFieldLocked('escalation_delay')}
                value={values.escalation_delay_unit}
                onChange={(val) => setFieldValue('escalation_delay_unit', val)}
                options={GRACE_WINDOW_UNITS}
              />
            </div>
          </Form.Item>
        );
        const readOnlyBox = (label, val) => (
          <div className="rounded-lg border" style={{ borderColor: '#e5e7eb', backgroundColor: '#f9fafb', padding: '10px 14px' }}>
            <p className="text-xs font-medium text-gray-500 m-0 mb-1">{label}</p>
            <p className="text-sm font-semibold text-gray-800 m-0">{val}</p>
          </div>
        );

        return (
          <Form layout="vertical" onFinish={handleSubmit}>
            {/* ── Header ── */}
            <div className="mb-2 flex items-start justify-between flex-wrap gap-2">
              <div>
                <h2 className="text-lg font-bold text-gray-800 m-0">
                  {values.parameter || 'Monitoring Parameter'}
                </h2>
                {values.machine_type_name && (
                  <p className="text-sm text-blue-600 font-medium m-0">
                    Machine: {values.machine_type_name}
                  </p>
                )}
              </div>
              {/* STATIC visual chips — not persisted */}
              <div className="flex items-center gap-1.5 flex-wrap">
                {values.mode === 'Manual' && (
                  <Tag color="blue" style={{ margin: 0, borderRadius: 12, fontSize: 10, fontWeight: 600 }}>
                    MANUAL
                  </Tag>
                )}
                <Tag color="red" style={{ margin: 0, borderRadius: 12, fontSize: 10, fontWeight: 600 }}>
                  CRITICAL
                </Tag>
                <Tag color="green" style={{ margin: 0, borderRadius: 12, fontSize: 10, fontWeight: 600 }}>
                  GROUP ELIGIBLE
                </Tag>
              </div>
            </div>

            {/* Two-column layout: form on the left (scrollable, pinned footer),
                rule info panel on the right */}
            <div className="flex gap-4 items-start">
              <div className={`min-w-0 ${rulesEnabled ? 'w-[calc(70%-8px)]' : 'w-full'}`}>
                {/* No fixed height / inner scroll here — the enclosing Modal (in
                    SubOperationDetails.js) is the single scroll container for the
                    whole popup, so this just lays out naturally instead of nesting
                    a second scrollbar. */}
                <div style={{ paddingRight: 6 }}>

            {one_machine_rule_enabled && (machine_setup_entries || []).length > 0 && (
              <div
                style={{
                  padding: 8,
                  marginBottom: 12,
                  background: '#eff6ff',
                  border: '1px solid #93c5fd',
                  borderRadius: 6,
                  fontSize: 12,
                  color: '#1e40af',
                }}
              >
                Machine type auto-selected from Machine Setup rule (One Machine Per Operation enabled).
              </div>
            )}

            {/* ── Section: PARAMETER DEFINITION ── */}
            <div className="mb-2 rounded-lg border bg-white" style={{ borderColor: '#e5e7eb', padding: '10px 12px' }}>
              {sectionHeader('Parameter Definition')}

              {/* Target + its own selector (Machine cascader or Location select) sit
                  together in one row so Target isn't visually orphaned from what it
                  controls, with Execution Mode filling out the rest of the width
                  instead of sitting alone on its own row. */}
              <Row gutter={16} className="mb-2">
                {!isRuleFieldHidden('target_type') && (
                <Col span={5}>
                  <Form.Item
                    label={
                      <span className="flex items-center gap-1">
                        Target
                        {isRuleFieldLocked('target_type') && (
                          <Lock className="w-3 h-3 text-yellow-600" />
                        )}
                      </span>
                    }
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      value={values.target_type}
                      disabled={isRuleFieldLocked('target_type')}
                      onChange={(val) => {
                        setFieldValue('target_type', val);
                        if (val === 'Location') {
                          setFieldValue('machine_type_id', null);
                          setFieldValue('machine_group_id', null);
                          setFieldValue('machine_id', null);
                          setFieldValue('machine_ids', []);
                          setFieldValue('machine_ids_csv', '');
                          setCascaderValue(null);
                          clearParam();
                          loadParametersForMachineGroup(null);
                        } else {
                          setFieldValue('location_area', null);
                        }
                      }}
                      options={TARGET_TYPE_OPTIONS.map((opt) =>
                        opt.value === 'Machine' &&
                        one_machine_rule_enabled &&
                        (machine_setup_entries || []).length === 0
                          ? {
                              ...opt,
                              disabled: true,
                              label: (
                                <Tooltip title="Machine Setup not configured for this operation yet.">
                                  {opt.label}
                                </Tooltip>
                              ),
                            }
                          : opt
                      )}
                    />
                  </Form.Item>
                </Col>
                )}
                <Col span={11}>
                  <Form.Item
                    label={
                      <span className="flex items-center gap-1">
                        {values.target_type === 'Location' ? 'Location' : 'Machine'}
                        {!!one_machine_rule_enabled &&
                          values.target_type !== 'Location' &&
                          (machine_setup_entries || []).length > 0 && (
                            <Tooltip title="Locked — auto-selected from Machine Setup (One Machine Per Operation rule is enabled in operation type config)">
                              <Lock className="w-3 h-3 text-yellow-600 cursor-help" />
                            </Tooltip>
                          )}
                      </span>
                    }
                    validateStatus={
                      (touched.machine_type_id && errors.machine_type_id) ||
                      (touched.machine_group_id && errors.machine_group_id) ? 'error' : ''
                    }
                    help={
                      (touched.machine_type_id && errors.machine_type_id) ||
                      (touched.machine_group_id && errors.machine_group_id)
                    }
                    style={{ marginBottom: 0 }}
                  >
                    {values.target_type === 'Location' ? (
                      <Select
                        placeholder={process_areas.length ? 'Select Area' : 'No area selected on this Process'}
                        value={values.location_area || undefined}
                        onChange={(val) => {
                          setFieldValue('location_area', val || null);
                          clearParam();
                          const areaId = warehouseAreas.find((a) => a.name === val)?.id || null;
                          loadParametersForLocation(areaId);
                        }}
                        allowClear
                        options={process_areas.map((a) => ({ label: a, value: a }))}
                      />
                    ) : one_machine_rule_enabled && (machine_setup_entries || []).length > 0 ? (
                      <LockedMachineSetupPanel
                        machines={setupMachineDetails.machines}
                        groupLabel={setupMachineDetails.groupLabel}
                      />
                    ) : (
                    <Cascader
                      value={cascaderValue || undefined}
                      placeholder="Select Category → Group → Machine"
                      showSearch
                      allowClear={!one_machine_rule_enabled}
                      disabled={!!one_machine_rule_enabled}
                      style={{ width: '100%' }}
                      popupClassName="cascader-wide-dropdown"
                      dropdownMenuColumnStyle={{ minWidth: 220 }}
                      expandTrigger="hover"
                      displayRender={(labels) => labels.join(' / ')}
                      onChange={(val, selectedOptions) => {
                        if (!val || val.length === 0) {
                          setCascaderValue(null);
                          setFieldValue('machine_type_id', null);
                          setFieldValue('machine_group_id', null);
                          setFieldValue('machine_type_name', null);
                          setFieldValue('machine_id', null);
                          setFieldValue('machine_name', null);
                          setFieldValue('machine_ids', []);
                          setFieldValue('machine_ids_csv', '');
                          clearParam();
                          loadParametersForMachineGroup(null);
                          return;
                        }
                        const leaf = selectedOptions?.[selectedOptions.length - 1];
                        const catId = leaf?._catId ?? val[0];
                        const grpId = leaf?._grpId ?? val[1];
                        const machineId = leaf?._machineId ?? null;
                        const machineName = leaf?._machineName ?? null;
                        const allIds = leaf?._allIds || (machineId ? [machineId] : []);
                        const label = `${leaf?._catLabel ?? ''} / ${leaf?._grpLabel ?? ''}`;
                        // Parameters (and the Expected Value/Range already picked for one)
                        // are scoped to the machine GROUP, not to which specific machines
                        // within it are selected — only reset them when the group itself
                        // changes. Otherwise switching e.g. one machine -> "All Machines"
                        // in the same group was silently wiping an already-set Expected
                        // Value/parameter for no reason.
                        const groupChanged = String(grpId) !== String(values.machine_group_id);
                        setCascaderValue(val);
                        setFieldValue('machine_type_id', catId);
                        setFieldValue('machine_group_id', grpId);
                        setFieldValue('machine_type_name', label);
                        setFieldValue('machine_id', machineId);
                        setFieldValue('machine_name', machineName);
                        setFieldValue('machine_ids', allIds);
                        setFieldValue('machine_ids_csv', allIds.join(','));
                        if (groupChanged) {
                          clearParam();
                          loadParametersForMachineGroup(grpId);
                        }
                      }}
                      options={machineTypeWithGroups.map((cat) => ({
                        label: cat.machine_category_name,
                        value: cat.machine_category_id,
                        children: (cat.machine_groups || []).map((g) => {
                          const machines = g.machines || [];
                          return {
                            label: g.machine_group_name,
                            value: g.machine_group_id,
                            children: [
                              {
                                label: `All Machines (${machines.length})`,
                                value: '__all__',
                                _catId: cat.machine_category_id,
                                _catLabel: cat.machine_category_name,
                                _grpId: g.machine_group_id,
                                _grpLabel: g.machine_group_name,
                                _machineId: machines[0]?.machine_id ?? null,
                                _machineName: machines[0]?.machine_name ?? null,
                                _allIds: machines.map((m) => m.machine_id),
                              },
                              ...machines.map((m) => ({
                                label: m.machine_code ? `${m.machine_name} (${m.machine_code})` : m.machine_name,
                                value: m.machine_id,
                                _catId: cat.machine_category_id,
                                _catLabel: cat.machine_category_name,
                                _grpId: g.machine_group_id,
                                _grpLabel: g.machine_group_name,
                                _machineId: m.machine_id,
                                _machineName: m.machine_name,
                                _allIds: [m.machine_id],
                              })),
                            ],
                          };
                        }),
                      }))}
                    />
                    )}
                  </Form.Item>
                </Col>
                {!isRuleFieldHidden('mode') && (
                <Col span={8}>
                  <Form.Item
                    label={
                      <span className="flex items-center gap-1">
                        Execution Mode
                        <Tooltip
                          title={
                            <div style={{ maxWidth: 260 }}>
                              Determines which Capture Modes and Reading Schedules are
                              available below — a fully automatic feed can't be typed in
                              by hand, and can't run on "whenever the operator feels like it."
                              <ul style={{ margin: '6px 0 0', paddingLeft: 16 }}>
                                <li><b>Manual</b> — any capture method, any schedule (operator does everything, e.g. reads a gauge and logs it whenever).</li>
                                <li><b>Semi-Automatic</b> — Photo/OCR or Integration only, any schedule (e.g. operator presses "Capture" to pull a sensor value every 30 sec).</li>
                                <li><b>Automatic</b> — Integration only, Count or Periodic only, no Adhoc (e.g. PLC feed every 10 sec with zero operator involvement).</li>
                              </ul>
                            </div>
                          }
                        >
                          <Info className="w-3 h-3 text-gray-400 cursor-help" />
                        </Tooltip>
                        {isRuleFieldLocked('mode') && (
                          <Lock className="w-3 h-3 text-yellow-600" />
                        )}
                      </span>
                    }
                    validateStatus={touched.mode && errors.mode ? 'error' : ''}
                    help={touched.mode && errors.mode}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      value={values.mode}
                      placeholder="Mode"
                      disabled={isRuleFieldLocked('mode')}
                      onChange={(val) => {
                        setFieldValue('mode', val);
                        const allowedCapture = CAPTURE_MODE_AVAILABILITY[val] || CAPTURE_MODE_AVAILABILITY.Manual;
                        const prunedCapture = (values.capture_modes || []).filter((m) => allowedCapture.includes(m));
                        setFieldValue('capture_modes', prunedCapture.length ? prunedCapture : [allowedCapture[0]]);
                        const allowedSchedule = READING_SCHEDULE_AVAILABILITY[val] || READING_SCHEDULE_AVAILABILITY.Manual;
                        if (!allowedSchedule.includes(values.reading_schedule_type)) {
                          setFieldValue('reading_schedule_type', 'Periodic');
                        }
                      }}
                      options={[
                        { value: 'Manual', label: 'Manual' },
                        { value: 'Semi-Automatic', label: 'Semi-Auto' },
                        { value: 'Automatic', label: 'Automatic' },
                      ]}
                    />
                  </Form.Item>
                </Col>
                )}
              </Row>

              {/* Capture Parameter Value gates the Parameter/Expected Value section below.
                  Track State Duration lives in its own module after Expected Value —
                  it needs the parameter's data type to know if it even applies.
                  The toggle gets its own compact header row (label + switch, no grid
                  column squeeze) so Parameter below can use the full row width instead
                  of being cramped next to it. */}
              {!isRuleFieldHidden('track_reading') && (
                <div className="flex items-center justify-between mb-2" style={{ padding: '2px 0' }}>
                  <span className="flex items-center gap-1 text-sm font-medium" style={{ color: '#374151' }}>
                    Capture Parameter Value
                    {isRuleFieldLocked('track_reading') && (
                      <Lock className="w-3 h-3 text-yellow-600" />
                    )}
                  </span>
                  <Switch
                    checked={!!values.track_reading}
                    // Track State Duration requires this on — see the module below —
                    // so it can't be switched back off while duration tracking is active.
                    disabled={isRuleFieldLocked('track_reading') || values.track_duration}
                    onChange={(val) => setFieldValue('track_reading', val)}
                    checkedChildren="Yes"
                    unCheckedChildren="No"
                  />
                </div>
              )}
              {values.track_reading && (
              <Row gutter={16} className="mb-2">
                <Col span={values.parameter_id && values._data_type_list?.length > 1 ? 15 : 24}>
                  <Form.Item
                    label="Parameter"
                    validateStatus={touched.parameter_id && errors.parameter_id ? 'error' : ''}
                    help={touched.parameter_id && errors.parameter_id}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      value={values.parameter_id}
                      placeholder={
                        // Machine context can come from the live selection OR be
                        // auto-resolved via the One Machine Per Operation rule
                        // (ruleLockedGroup) — don't block on Formik's own field alone.
                        values.target_type === 'Location' ||
                        values.machine_group_id ||
                        ruleLockedGroup
                          ? 'Select Parameter'
                          : 'Select Machine first'
                      }
                      disabled={
                        values.target_type === 'Machine' &&
                        !values.machine_group_id &&
                        !ruleLockedGroup
                      }
                      loading={machineParamsLoading}
                      showSearch
                      allowClear
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      onChange={(val, option) => {
                        setFieldValue('parameter_id', val || null);
                        if (val) {
                          setFieldValue('parameter', option.label);
                          setFieldValue('parameter_unit', option.parameter_unit);
                          setFieldValue('quantity', null);
                          setFieldValue('min_value', null);
                          setFieldValue('max_value', null);
                          setFieldValue('exp_value', null);
                          // auto-select data_type if only one, else clear for user to pick
                          const dtList = Array.isArray(option.data_type)
                            ? option.data_type
                            : option.data_type
                            ? [option.data_type]
                            : [];
                          setFieldValue('_data_type_list', dtList);
                          setFieldValue(
                            'parameter_data_type',
                            dtList.length === 1 ? dtList[0] : null
                          );
                        } else {
                          setFieldValue('parameter', null);
                          setFieldValue('parameter_unit', null);
                          setFieldValue('parameter_data_type', null);
                          setFieldValue('_data_type_list', []);
                        }
                      }}
                      options={machineParameters?.map((p) => ({
                        label: p.parameter_name,
                        value: p.id,
                        parameter_unit: p.unit,
                        data_type: p.data_type,
                      }))}
                    />
                  </Form.Item>
                </Col>
                {/* Data Type selector — shown when parameter has multiple data types */}
                {values.parameter_id && values._data_type_list?.length > 1 && (
                  <Col span={9}>
                    <Form.Item label="Data Type" style={{ marginBottom: 0 }}>
                      <Select
                        allowClear
                        placeholder="Select Data Type"
                        value={values.parameter_data_type}
                        onChange={(val) => {
                          setFieldValue('parameter_data_type', val || null);
                          setFieldValue('quantity', null);
                          setFieldValue('min_value', null);
                          setFieldValue('max_value', null);
                          setFieldValue('exp_value', null);
                        }}
                        options={values._data_type_list.map((dt) => ({ label: dt, value: dt }))}
                      />
                    </Form.Item>
                  </Col>
                )}
              </Row>
              )}

              {/* Row: Unit (reading only), Capture Mode */}
              <Row gutter={16} className="mb-2">
                {values.track_reading && (
                  <Col span={12}>
                    <Form.Item label="Unit" style={{ marginBottom: 0 }}>
                      <Input
                        placeholder="Unit"
                        value={values.parameter_unit || ''}
                        disabled
                      />
                    </Form.Item>
                  </Col>
                )}
                {!isRuleFieldHidden('capture_modes') && (
                <Col span={values.track_reading ? 12 : 24}>
                  <Form.Item
                    label={
                      <span className="flex items-center gap-1">
                        Capture Mode
                        {isRuleFieldLocked('capture_modes') && (
                          <Lock className="w-3 h-3 text-yellow-600" />
                        )}
                      </span>
                    }
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      mode="multiple"
                      disabled={isRuleFieldLocked('capture_modes')}
                      value={values.capture_modes}
                      onChange={(val) => {
                        setFieldValue('capture_modes', val);
                        // No Manual capture ⇒ no human timing tolerance to model.
                        if (!val.includes('Manual')) {
                          setFieldValue('grace_window_value', 0);
                        }
                        // Integration Assisted dropped ⇒ its service selection no longer applies.
                        if (!val.includes('Integration Assisted')) {
                          setFieldValue('integration_service', null);
                        }
                      }}
                      options={CAPTURE_MODE_OPTIONS.filter((o) =>
                        (CAPTURE_MODE_AVAILABILITY[values.mode] || CAPTURE_MODE_AVAILABILITY.Manual).includes(o.value)
                      )}
                      placeholder="Select one or more"
                    />
                  </Form.Item>
                </Col>
                )}
              </Row>

              {/* Expected Value (Boolean/String) or Min/Max range */}
              {values.track_reading && values.parameter_id && effectiveDataType && (
                effectiveDataType === 'Boolean' ||
                effectiveDataType === 'String' ? (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label="Expected Value"
                        validateStatus={touched.exp_value && errors.exp_value ? 'error' : ''}
                        help={touched.exp_value && errors.exp_value}
                        style={{ marginBottom: 0 }}
                      >
                        {effectiveDataType === 'Boolean' ? (
                          <Select
                            className="w-full"
                            placeholder="Select Expected Value"
                            value={values.exp_value}
                            onChange={(val) => setFieldValue('exp_value', val)}
                            options={[
                              { label: 'True', value: 'True' },
                              { label: 'False', value: 'False' },
                            ]}
                          />
                        ) : (
                          <Input
                            placeholder="Enter Expected Value"
                            value={values.exp_value}
                            onChange={(e) => setFieldValue('exp_value', e.target.value)}
                          />
                        )}
                      </Form.Item>
                    </Col>
                    {!isRuleFieldHidden('quantity') && (
                    <Col span={12}>
                      <Form.Item
                        label={
                          <span className="flex items-center gap-1">
                            Quantity
                            {isRuleFieldLocked('quantity') && (
                              <Lock className="w-3 h-3 text-yellow-600" />
                            )}
                          </span>
                        }
                        validateStatus={touched.quantity && errors.quantity ? 'error' : ''}
                        help={touched.quantity && errors.quantity}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber
                          className="w-full"
                          placeholder="Qty"
                          disabled={isRuleFieldLocked('quantity')}
                          value={values.quantity}
                          addonAfter={
                            <Tooltip title={values.parameter_unit}>
                              <span>
                                {values.parameter_unit?.length > 4
                                  ? values.parameter_unit?.slice(0, 4) + '…'
                                  : values.parameter_unit}
                              </span>
                            </Tooltip>
                          }
                          onChange={(val) => setFieldValue('quantity', val)}
                        />
                      </Form.Item>
                    </Col>
                    )}
                  </Row>
                ) : (
                  <>
                    <Row gutter={16} className="mb-2">
                      <Col span={12}>
                        <Form.Item label="Lower Limit" style={{ marginBottom: 0 }}>
                          <InputNumber
                            className="w-full"
                            placeholder="Min"
                            value={values.min_value}
                            onChange={(val) => setFieldValue('min_value', val)}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label="Upper Limit"
                          validateStatus={touched.max_value && errors.max_value ? 'error' : ''}
                          help={touched.max_value && errors.max_value}
                          style={{ marginBottom: 0 }}
                        >
                          <InputNumber
                            className="w-full"
                            placeholder="Max"
                            value={values.max_value}
                            onChange={(val) => setFieldValue('max_value', val)}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={16}>
                      {!isRuleFieldHidden('quantity') && (
                      <Col span={12}>
                        <Form.Item
                          label={
                            <span className="flex items-center gap-1">
                              Quantity
                              {isRuleFieldLocked('quantity') && (
                                <Lock className="w-3 h-3 text-yellow-600" />
                              )}
                            </span>
                          }
                          validateStatus={touched.quantity && errors.quantity ? 'error' : ''}
                          help={touched.quantity && errors.quantity}
                          style={{ marginBottom: 0 }}
                        >
                          <InputNumber
                            className="w-full"
                            placeholder="Qty"
                            disabled={isRuleFieldLocked('quantity')}
                            value={values.quantity}
                            addonAfter={
                              <Tooltip title={values.parameter_unit}>
                                <span>
                                  {values.parameter_unit?.length > 4
                                    ? values.parameter_unit?.slice(0, 4) + '…'
                                    : values.parameter_unit}
                                </span>
                              </Tooltip>
                            }
                            onChange={(val) => setFieldValue('quantity', val)}
                          />
                        </Form.Item>
                      </Col>
                      )}
                      {!isRuleFieldHidden('deviation_rule') && (
                      <Col span={12}>
                        <Form.Item
                          label={
                            <span className="flex items-center gap-1">
                              Deviation Rule
                              {isRuleFieldLocked('deviation_rule') && (
                                <Lock className="w-3 h-3 text-yellow-600" />
                              )}
                            </span>
                          }
                          style={{ marginBottom: 0 }}
                        >
                          <Select
                            className="w-full"
                            disabled={isRuleFieldLocked('deviation_rule')}
                            value={values.deviation_rule || 'none'}
                            onChange={(val) => setFieldValue('deviation_rule', val)}
                            options={DEVIATION_RULE_OPTIONS}
                          />
                        </Form.Item>
                      </Col>
                      )}
                    </Row>
                  </>
                )
              )}

              {/* Track State Duration — a separate module below Expected Value: it only
                  makes sense for a Boolean parameter (how long that state persists), so
                  it needs the parameter/data-type already resolved above. */}
              {values.track_reading && values.parameter_id && !isRuleFieldHidden('track_duration') && (
                <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed #e5e7eb' }}>
                  {/* Own compact header row for the toggle, same as Capture Parameter
                      Value above — Min/Max Duration get a clean full-width row below
                      instead of squeezing into a 3rd column next to the switch. */}
                  <div className="flex items-center justify-between mb-2" style={{ padding: '2px 0' }}>
                    <span className="flex items-center gap-1 text-sm font-medium" style={{ color: '#374151' }}>
                      Track State Duration
                      {isRuleFieldLocked('track_duration') && (
                        <Lock className="w-3 h-3 text-yellow-600" />
                      )}
                    </span>
                    <Tooltip
                      title={
                        effectiveDataType !== 'Boolean'
                          ? 'Only available for Boolean parameters (tracks how long a state lasts).'
                          : undefined
                      }
                    >
                      <Switch
                        checked={!!values.track_duration}
                        disabled={isRuleFieldLocked('track_duration') || effectiveDataType !== 'Boolean'}
                        onChange={(val) => {
                          setFieldValue('track_duration', val);
                          // Duration tracking implies capturing the value whose state is being timed.
                          if (val) setFieldValue('track_reading', true);
                        }}
                        checkedChildren="Yes"
                        unCheckedChildren="No"
                      />
                    </Tooltip>
                  </div>
                  {values.track_duration && (
                    <Row gutter={16}>
                      <Col span={12}>
                        <Form.Item
                          label="Min Duration"
                          validateStatus={touched.duration_min_value && errors.duration_min_value ? 'error' : ''}
                          help={touched.duration_min_value && errors.duration_min_value}
                          style={{ marginBottom: 0 }}
                        >
                          <InputNumber
                            className="w-full"
                            min={0}
                            step="any"
                            value={values.duration_min_value}
                            onChange={(val) => setFieldValue('duration_min_value', val)}
                            placeholder="Min"
                          />
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label="Max Duration"
                          validateStatus={touched.duration_max_value && errors.duration_max_value ? 'error' : ''}
                          help={touched.duration_max_value && errors.duration_max_value}
                          style={{ marginBottom: 0 }}
                        >
                          <div className="flex gap-2">
                            <InputNumber
                              className="flex-1"
                              min={0}
                              step="any"
                              value={values.duration_max_value}
                              onChange={(val) => setFieldValue('duration_max_value', val)}
                              placeholder="Max"
                            />
                            <Select
                              style={{ width: 90 }}
                              value={values.duration_unit}
                              onChange={(val) => setFieldValue('duration_unit', val)}
                              options={DURATION_UNITS}
                            />
                          </div>
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                </div>
              )}

              {/* Integration Assisted source — one shared external Service (Configuration
                  → System Integration → URL Configuration) for the whole entry, applying
                  to whichever of reading/duration is tracked. */}
              {(values.capture_modes || []).includes('Integration Assisted') &&
                (values.track_reading || values.track_duration) && (
                <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed #e5e7eb' }}>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                    Integration Service
                    {isRuleFieldLocked('integration_service') && (
                      <Lock className="w-3 h-3 text-yellow-600" />
                    )}
                  </p>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Select
                        className="w-full"
                        disabled={isRuleFieldLocked('integration_service')}
                        placeholder={integrationServices.length ? 'Select Service' : 'No services configured'}
                        value={values.integration_service || undefined}
                        onChange={(val) => setFieldValue('integration_service', val || null)}
                        allowClear
                        options={integrationServices.map((s) => ({ label: s.type, value: s.type }))}
                      />
                    </Col>
                  </Row>
                </div>
              )}
            </div>

            {/* ── Section: MONITORING SCHEDULE ── */}
            <div className="mb-2 rounded-lg border bg-white" style={{ borderColor: '#e5e7eb', padding: '10px 12px' }}>
              {sectionHeader('Monitoring Schedule')}
              <Row gutter={16} className="mb-2">
                {!isRuleFieldHidden('reading_schedule_type') && (
                <Col span={12}>
                  <Form.Item
                    label={
                      <span className="flex items-center gap-1">
                        Reading Schedule
                        {isRuleFieldLocked('reading_schedule_type') && (
                          <Lock className="w-3 h-3 text-yellow-600" />
                        )}
                      </span>
                    }
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      disabled={isRuleFieldLocked('reading_schedule_type')}
                      value={values.reading_schedule_type}
                      onChange={(val) => {
                        setFieldValue('reading_schedule_type', val);
                        if (val !== 'Periodic') setFieldValue('monitoring_frequency', null);
                        if (val !== 'Count') setFieldValue('reading_count', null);
                      }}
                      options={READING_SCHEDULE_OPTIONS.filter((o) =>
                        (READING_SCHEDULE_AVAILABILITY[values.mode] || READING_SCHEDULE_AVAILABILITY.Manual).includes(o.value)
                      )}
                    />
                  </Form.Item>
                </Col>
                )}
                {!isRuleFieldHidden('grace_window') && (
                <Col span={12}>
                  <Form.Item
                    label={
                      <span className="flex items-center gap-1">
                        Grace Window
                        <Tooltip title="A reading taken any time from when it's due up to this long afterward still counts as on-time. Escalation Delay only starts counting once this window expires.">
                          <Info className="w-3 h-3 text-gray-400 cursor-help" />
                        </Tooltip>
                        {isRuleFieldLocked('grace_window') && (
                          <Lock className="w-3 h-3 text-yellow-600" />
                        )}
                      </span>
                    }
                    validateStatus={
                      (touched.grace_window_value && errors.grace_window_value) ||
                      (touched.grace_window_unit && errors.grace_window_unit)
                        ? 'error'
                        : ''
                    }
                    help={
                      (touched.grace_window_value && errors.grace_window_value) ||
                      (touched.grace_window_unit && errors.grace_window_unit)
                    }
                    style={{ marginBottom: 0 }}
                  >
                    {(() => {
                      const noManualCapture = !(values.capture_modes || []).includes('Manual');
                      const forceZero = noManualCapture || !graceEscalationApply;
                      return (
                        <div className="flex gap-2">
                          <InputNumber
                            className="flex-1"
                            min={0}
                            step="any"
                            disabled={isRuleFieldLocked('grace_window') || forceZero}
                            value={forceZero ? 0 : values.grace_window_value}
                            onChange={(val) => setFieldValue('grace_window_value', val)}
                            placeholder="Amount"
                          />
                          <Select
                            style={{ width: 130 }}
                            disabled={isRuleFieldLocked('grace_window') || forceZero}
                            value={values.grace_window_unit}
                            onChange={(val) => setFieldValue('grace_window_unit', val)}
                            options={GRACE_WINDOW_UNITS}
                          />
                        </div>
                      );
                    })()}
                    {!graceEscalationApply && (
                      <p className="text-xs mt-1" style={{ color: '#9ca3af' }}>
                        {values.reading_schedule_type === 'Adhoc'
                          ? 'Not applicable for Adhoc — no grace window or escalation.'
                          : 'No operation duration set — behaves like Adhoc (no grace window or escalation).'}
                      </p>
                    )}
                  </Form.Item>
                </Col>
                )}
              </Row>

              {/* Periodic — Frequency (existing math; naturally "continuous" when the
                  operation has no known/bounded duration — same messaging as before). */}
              {values.reading_schedule_type === 'Periodic' && (
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label={
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Frequency
                        </span>
                      }
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        className="w-full"
                        placeholder="Enter frequency"
                        min={1}
                        value={
                          frequencyUnit === 'min' && values.monitoring_frequency
                            ? +(values.monitoring_frequency / 60).toFixed(2)
                            : values.monitoring_frequency
                        }
                        onChange={(val) => {
                          if (!val) {
                            setFieldValue('monitoring_frequency', null);
                            setFieldValue('reading_count', null);
                            return;
                          }
                          const freqInSec = frequencyUnit === 'min' ? Math.round(val * 60) : val;
                          setFieldValue('monitoring_frequency', freqInSec);
                          if (hasKnownDuration && freqInSec > 0 && freqInSec <= operation_duration) {
                            setFieldValue(
                              'reading_count',
                              Math.floor(operation_duration / freqInSec)
                            );
                          } else if (freqInSec > 0) {
                            setFieldValue('reading_count', null);
                          }
                        }}
                        addonAfter={
                          <Select
                            value={frequencyUnit}
                            onChange={(u) => setFrequencyUnit(u)}
                            style={{ width: 70 }}
                            options={[
                              { value: 'sec', label: 'sec' },
                              { value: 'min', label: 'min' },
                            ]}
                          />
                        }
                      />
                    </Form.Item>
                    {values.reading_count > 0 && operation_duration > 0 && (
                      <p className="text-xs mt-1" style={{ color: '#6b7280' }}>
                        {operation_duration}s ÷ {values.reading_count} ={' '}
                        {Math.round(operation_duration / values.reading_count)}s/reading
                      </p>
                    )}
                    {values.monitoring_frequency > 0 && !values.reading_count && (
                      <p className="text-xs mt-1" style={{ color: '#6b7280' }}>
                        No. of readings:{' '}
                        {!hasKnownDuration
                          ? 'Until completed. Readings continue until the operation is completed.'
                          : 'Cannot derive count — frequency exceeds operation duration.'}
                      </p>
                    )}
                  </Col>
                  <Col span={12}>
                    {escalationDelayField}
                  </Col>
                </Row>
              )}

              {/* Count — explicit No. of Readings. Grace/Escalation only apply when the
                  operation has a known duration (needed to derive an interval); without
                  one, Count behaves like Adhoc for those two fields. */}
              {values.reading_schedule_type === 'Count' && (
                <Row gutter={16}>
                  <Col span={hasKnownDuration ? 12 : 24}>
                    <Form.Item
                      label="No. of Readings"
                      validateStatus={
                        touched.reading_count && errors.reading_count ? 'error' : ''
                      }
                      help={touched.reading_count && errors.reading_count}
                      style={{ marginBottom: 0 }}
                    >
                      <InputNumber
                        className="w-full"
                        placeholder="Readings"
                        value={values.reading_count}
                        min={1}
                        onChange={(val) => {
                          if (!isRuleFieldLocked('mode')) {
                            setFieldValue('mode', 'Manual');
                          }
                          setFieldValue('reading_count', val || null);
                          if (val && hasKnownDuration) {
                            setFieldValue(
                              'monitoring_frequency',
                              Math.round(operation_duration / val)
                            );
                          } else {
                            setFieldValue('monitoring_frequency', null);
                          }
                        }}
                      />
                    </Form.Item>
                  </Col>
                  {hasKnownDuration && (
                    <Col span={12}>
                      {escalationDelayField}
                    </Col>
                  )}
                </Row>
              )}

              {/* Adhoc — no fixed count/frequency; operator (or an event) decides when.
                  No grace window or escalation delay apply here. */}
              {values.reading_schedule_type === 'Adhoc' && (
                <>
                  {!isRuleFieldHidden('trigger_type') && (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        label={
                          <span className="flex items-center gap-1">
                            Trigger Type
                            {isRuleFieldLocked('trigger_type') && (
                              <Lock className="w-3 h-3 text-yellow-600" />
                            )}
                          </span>
                        }
                        style={{ marginBottom: 0 }}
                      >
                        <Select
                          disabled={isRuleFieldLocked('trigger_type')}
                          value={values.trigger_type}
                          onChange={(val) => setFieldValue('trigger_type', val)}
                          options={[
                            { value: 'User', label: 'User' },
                            { value: 'Event Triggered', label: 'Event Triggered' },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  )}
                  <div
                    className="mt-2 rounded-md text-xs"
                    style={{
                      background: '#eff6ff',
                      border: '1px solid #bfdbfe',
                      color: '#1e40af',
                      padding: '8px 10px',
                    }}
                  >
                    No fixed count or frequency, and no grace window or escalation delay —
                    the operator can take multiple readings at any time until the operation ends.
                  </div>
                </>
              )}

              {/* Is Start / Is End — optional manual readings at operation
                  start and/or end. Default false; the user sets them. */}
              <Row gutter={16} className="mt-2">
                <Col span={12}>
                  <Form.Item label="Is Start" style={{ marginBottom: 0 }}>
                    <Switch
                      checked={!!values.startTime}
                      onChange={(val) => setFieldValue('startTime', val)}
                      checkedChildren="Yes"
                      unCheckedChildren="No"
                    />
                    <span className="ml-2 text-xs" style={{ color: '#6b7280' }}>
                      Take a reading at operation start
                    </span>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Is End" style={{ marginBottom: 0 }}>
                    <Switch
                      checked={!!values.endTime}
                      onChange={(val) => setFieldValue('endTime', val)}
                      checkedChildren="Yes"
                      unCheckedChildren="No"
                    />
                    <span className="ml-2 text-xs" style={{ color: '#6b7280' }}>
                      Take a reading at operation end
                    </span>
                  </Form.Item>
                </Col>
              </Row>
            </div>

            {/* ── Section: Instruction ── */}
            {!isRuleFieldHidden('instruction') && (
            <div className="mb-2 rounded-lg border bg-white" style={{ borderColor: '#e5e7eb', padding: '10px 12px' }}>
              {sectionHeader('Instruction')}
              <Form.Item
                label={
                  <span className="flex items-center gap-1">
                    Instruction{' '}
                    {lockedRuleFields?.instruction && (
                      <Lock className="w-3 h-3 text-yellow-600" />
                    )}
                  </span>
                }
                style={{ marginBottom: 0 }}
              >
                <TextArea
                  rows={2}
                  placeholder="Instruction"
                  value={values.instruction}
                  disabled={!!lockedRuleFields?.instruction}
                  onChange={(e) => setFieldValue('instruction', e.target.value)}
                />
              </Form.Item>
            </div>
            )}

            {/* ── Rules Panel — hidden when Rules app-setting is off ── */}
            {rulesEnabled && filteredSettings?.length > 0 && (
              <div
                className="rounded-lg border w-full mb-4"
                style={{ borderColor: '#e5e7eb', backgroundColor: '#f9fafb', padding: '12px 16px' }}
              >
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                  Rules
                </p>
                <div className="flex flex-col gap-1">
                  {filteredSettings?.map((setting, idx) => (
                    <div key={setting.id ?? idx} className="flex items-center justify-between py-1">
                      <span className="text-sm font-medium" style={{ color: '#9ca3af' }}>
                        {setting.module}
                      </span>
                      <Tag
                        color={setting.status ? 'green' : 'default'}
                        style={{ margin: 0, minWidth: 36, textAlign: 'center' }}
                      >
                        {setting.status ? 'Yes' : 'No'}
                      </Tag>
                    </div>
                  ))}
                </div>
              </div>
            )}
                </div>
              </div>

              {/* ── RIGHT: rule info panel — only when rulesEnabled ── */}
              {rulesEnabled && (
                <div className="w-[30%] min-w-0 flex-shrink-0">
                  <div
                    className="rounded-lg border bg-white mb-4"
                    style={{ borderColor: '#e5e7eb', padding: '14px 16px' }}
                  >
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 m-0">
                      Runtime Execution Rules
                    </p>
                    <div className="flex flex-col gap-2">
                      {runtimeRules.map(({ key, label }) => {
                        const er = values.execution_rules || {};
                        const checked = er[key] !== undefined && er[key] !== null
                          ? !!er[key]
                          : !!ruleVal(key, runtimeDefaults[key]);
                        const locked = isRuntimeRuleLocked(key);
                        return (
                          <Checkbox
                            key={key}
                            checked={checked}
                            disabled={locked}
                            onChange={(e) =>
                              setFieldValue('execution_rules', {
                                ...(values.execution_rules || {}),
                                [key]: e.target.checked,
                              })
                            }
                          >
                            <span className="inline-flex items-center gap-1">
                              {label}
                              {locked && <Lock className="w-3 h-3 text-yellow-600" />}
                            </span>
                          </Checkbox>
                        );
                      })}
                    </div>
                    <div className="mt-3 flex flex-col gap-2">
                      {readOnlyBox('Execution Mode', executionModeLabel)}
                      {readOnlyBox('Verification', verification)}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Full-width footer — Save spans the whole card */}
            <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 10, marginTop: 10 }}>
              {Object.keys(errors || {}).length > 0 && (
                <div
                  style={{
                    marginBottom: 8,
                    padding: 8,
                    background: '#fef2f2',
                    border: '1px dashed #fca5a5',
                    borderRadius: 6,
                    fontSize: 11,
                    color: '#991b1b',
                  }}
                >
                  Fix these fields: {Object.keys(errors).join(', ')}
                </div>
              )}
              <Button
                type="primary"
                htmlType="submit"
                block
                disabled={
                  !!one_machine_rule_enabled &&
                  values.target_type !== 'Location' &&
                  (machine_setup_entries || []).length === 0
                }
              >
                {editIndex !== null ? 'Update' : 'Save Configuration'}
              </Button>
              {!!one_machine_rule_enabled &&
                values.target_type !== 'Location' &&
                (machine_setup_entries || []).length === 0 && (
                  <div style={{ fontSize: 12, color: '#991b1b', marginTop: 6, textAlign: 'center' }}>
                    Please setup machines first in the Machine Setup tab before adding monitoring.
                  </div>
              )}
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}

export const MonitoringCard = ({ fromView, item, onEdit, onDelete, operation_duration = 0 }) => {
  const {
    machine_name,
    machine_type_name,
    target_type,
    location_area,
    parameter,
    parameter_id,
    parameter_unit,
    parameter_data_type,
    sensor,
    sensor_id,
    quantity,
    exp_value,
    reading_count,
    monitoring_frequency,
    min_value,
    max_value,
    instruction,
    mode,
    capture_modes,
    track_reading,
    track_duration,
    duration_min_value,
    duration_max_value,
    duration_unit,
    integration_service,
    reading_schedule_type,
    grace_window,
    grace_window_value,
    grace_window_unit,
    escalation_delay,
    startTime,
    endTime,
    machine_register,
    machine_registers,
    _from_rule,
  } = item;

  const { isDark } = useTheme();

  // Use machine_registers if available, otherwise fall back to machine_register (backward compatibility)
  const registerData = machine_registers || machine_register;

  // See normalizeExpValue's comment above — some read paths return this as a
  // raw JSON boolean rather than the 'True'/'False' strings it's stored/edited
  // as, which both fails to render (React doesn't render bare booleans) and,
  // for a saved `false`, was indistinguishable from "not set" under `exp_value ? ...`.
  const expValueDisplay = normalizeExpValue(exp_value);

  // Calculate reading interval time in seconds
  const readingIntervalTime =
    reading_count && operation_duration ? Math.round(operation_duration / reading_count) : 0;

  const showLabel = (label, id) => label || `${id ? 'ID: ' + id : 'N/A'}`;
  const graceWindow = parseGraceWindow(
    grace_window,
    grace_window_value,
    grace_window_unit
  );

  return (
    <div
      className={`rounded-lg border shadow-sm hover:shadow-md transition p-3 text-sm ${
        isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-blue-100 rounded-md">
            <Activity className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <p
              className="font-semibold text-gray-900 leading-tight truncate text-base w-64"
              title={showLabel(parameter, parameter_id)}
            >
              {showLabel(parameter, parameter_id)}
            </p>
            {target_type === 'Location' ? (
              location_area && (
                <p className="text-xs text-blue-600 font-medium">
                  Location: {location_area}
                </p>
              )
            ) : (
              (machine_type_name || machine_name) && (
                <p className="text-xs text-blue-600 font-medium">
                  Machine: {machine_type_name || machine_name}
                </p>
              )
            )}
            <p className="text-xs text-gray-600">
              {parameter_unit ? `Unit: ${parameter_unit}` : 'No unit'}
              {parameter_data_type ? ` · ${parameter_data_type}` : ''}
            </p>
          </div>
        </div>

        {_from_rule && (
          <div
            className="flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium"
            style={{ backgroundColor: '#fef9c3', color: '#92400e' }}
            title="Locked — assigned from Operation Type rule"
          >
            <Lock className="w-3 h-3" />
            Rule
          </div>
        )}

        {!fromView && (
          <div className="flex items-center gap-1">
            {onEdit && (
              <button
                type="button"
                className="p-1.5 rounded-md hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-200"
                onClick={() => onEdit()}
                title="Edit monitoring parameter"
              >
                <Edit3 className="w-4 h-4 text-blue-600" />
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                className="p-1.5 rounded-md hover:bg-red-50 transition-colors border border-transparent hover:border-red-200"
                onClick={() => onDelete()}
                title="Delete monitoring parameter"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            )}
          </div>
        )}
      </div>

      {/* Mode + tags row */}
      <div className="flex items-center gap-2 my-2 flex-wrap">
        <span className="flex items-center gap-1">
          <CheckCircle className="w-3.5 h-3.5 text-green-600" />
          <span className="text-xs text-gray-700 font-medium">
            Mode: {mode || 'Manual'}
          </span>
        </span>
        {monitoring_frequency && (
          <Tag color="purple" className="text-xs">
            <Clock className="w-3 h-3 inline mr-0.5" />
            {monitoring_frequency} min
          </Tag>
        )}
        {reading_schedule_type && (
          <Tag color="blue" className="text-xs">
            Schedule: {reading_schedule_type}
          </Tag>
        )}
        {Array.isArray(capture_modes) && capture_modes.length > 0 && (
          <Tag color="cyan" className="text-xs">
            Capture: {capture_modes.join(', ')}
          </Tag>
        )}
        {track_reading && (
          <Tag color="green" className="text-xs">Capture Parameter Value</Tag>
        )}
        {track_duration && (
          <Tag color="green" className="text-xs">
            Track State Duration
            {(duration_min_value !== null && duration_min_value !== undefined) ||
             (duration_max_value !== null && duration_max_value !== undefined)
              ? `: ${duration_min_value ?? 'N/A'} - ${duration_max_value ?? 'N/A'} ${duration_unit || ''}`
              : ''}
          </Tag>
        )}
        {escalation_delay ? (
          <>
            <Tag color="gold" className="text-xs">
              Grace: {formatGraceWindow(graceWindow.value, graceWindow.unit)}
            </Tag>
            <Tag color="gold" className="text-xs">
              Escalation: {escalation_delay}
            </Tag>
          </>
        ) : (
          <Tag color="default" className="text-xs">No Grace / Escalation</Tag>
        )}
      </div>

      {/* Content */}
      <div className="grid grid-cols-2 gap-3">
        {/* Show Expected Value if exists (including a saved "False"), otherwise show Quantity */}
        {expValueDisplay !== null && expValueDisplay !== undefined ? (
          <div className="bg-purple-50 rounded-md p-2.5">
            <p className="text-xs text-purple-700 font-medium mb-1">Expected Value</p>
            <p className="text-purple-900 font-semibold">{expValueDisplay}</p>
          </div>
        ) : (
          <div className="bg-indigo-50 rounded-md p-2.5">
            <p className="text-xs text-indigo-700 font-medium mb-1">Quantity</p>
            <p className="text-indigo-900 font-semibold">
              {quantity}{' '}
              <span className="text-sm font-normal text-indigo-700">
                {parameter_unit || 'Unit'}
              </span>
            </p>
          </div>
        )}

        {reading_count && (
          <div className="bg-purple-50 rounded-md p-2.5">
            <p className="text-xs text-purple-700 font-medium mb-1">Reading Count</p>
            <p className="text-purple-900 font-semibold flex items-center gap-1">
              <Clock className="w-3 h-3" /> {reading_count}
            </p>
            {readingIntervalTime > 0 && (
              <p className="text-xs text-purple-600 mt-1">Interval: {readingIntervalTime} sec</p>
            )}
          </div>
        )}

        {(mode === 'Automatic' || mode === 'Semi-Automatic') && sensor_id && (
          <div className="bg-green-50 rounded-md p-2.5">
            <p className="text-xs text-green-700 font-medium mb-1">Sensor</p>
            <p
              className="text-green-900 font-semibold truncate flex items-center gap-1"
              title={showLabel(sensor, sensor_id)}
            >
              <Gauge className="w-3 h-3" />
              {showLabel(sensor, sensor_id)}
            </p>
          </div>
        )}

        {/* Range only applies to numeric parameters — never alongside a Boolean/String
            Expected Value. Bug fix: Number(null) is 0, so `Number(min_value) >= 0` was
            true even when min/max were never set; check for presence instead. */}
        {(expValueDisplay === null || expValueDisplay === undefined) &&
          (min_value !== null && min_value !== undefined ||
            max_value !== null && max_value !== undefined) && (
          <div className="bg-orange-50 rounded-md p-2.5">
            <p className="text-xs text-orange-700 font-medium mb-1">Range</p>
            <p className="text-orange-900 font-semibold">
              {min_value !== null && min_value !== undefined ? min_value : 'N/A'} -{' '}
              {max_value !== null && max_value !== undefined ? max_value : 'N/A'}{' '}
              <span className="text-sm font-normal text-orange-700">{parameter_unit || ''}</span>
            </p>
          </div>
        )}

        {(startTime || endTime) && (
          <div className="bg-gray-50 rounded-md p-2.5 col-span-2">
            <p className="text-xs text-gray-600 font-medium mb-1">Interval Settings</p>
            <div className="flex gap-2">
              {startTime && (
                <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-blue-100 text-blue-700">
                  <Clock className="w-3 h-3 mr-1" />
                  Start: {startTime}
                </span>
              )}
              {endTime && (
                <span className="inline-flex items-center px-2 py-1 rounded text-xs bg-red-100 text-red-700">
                  <Clock className="w-3 h-3 mr-1" />
                  End: {endTime}
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Instruction */}
      {instruction && (
        <div className={`mt-3 rounded-md p-2.5 ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
          <p className={`text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
            Instruction
          </p>
          <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
            {instruction}
          </p>
        </div>
      )}

      {/* Machine & Registers */}
      {registerData && registerData.length > 0 && (
        <div
          className="mt-3 rounded-md p-2.5"
          style={{ backgroundColor: isDark ? '#1e293b' : '#f0f9ff' }}
        >
          <p className="text-xs font-medium mb-1" style={{ color: isDark ? '#93c5fd' : '#0369a1' }}>
            Machines & Registers
          </p>
          <div className="overflow-x-auto pb-1">
            <div className="flex gap-3" style={{ minWidth: 'fit-content' }}>
              {registerData.map((machineReg, index) => (
                <div key={index} className="flex-shrink-0" style={{ minWidth: '200px' }}>
                  <Tag color="blue" className="mb-1">
                    {machineReg.machine_name || `Machine ${machineReg.machine_id}`}
                  </Tag>
                  {((machineReg.register_ids && machineReg.register_ids.length > 0) ||
                    (machineReg.registers && machineReg.registers.length > 0)) && (
                    <div className="flex flex-wrap gap-1">
                      {(machineReg.registers || machineReg.register_ids)?.map((reg, idx) => {
                        let displayName;
                        let key;

                        // If reg is an object with id and name
                        if (typeof reg === 'object' && reg !== null) {
                          key = reg.id || reg.register_id || idx;
                          displayName = reg.name || reg.register_name || `#${key}`;
                        } else {
                          // If reg is just an ID, check for corresponding name in register_names
                          key = reg;
                          const nameFromArray = machineReg.register_names?.[idx];
                          displayName = nameFromArray || `#${reg}`;
                        }

                        return (
                          <Tag key={key} color="cyan" className="text-xs">
                            {displayName}
                          </Tag>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Integration Service (Integration Assisted capture only) */}
      {integration_service && (
        <div
          className="mt-3 rounded-md p-2.5"
          style={{ backgroundColor: isDark ? '#1e293b' : '#f0f9ff' }}
        >
          <p className="text-xs font-medium mb-1" style={{ color: isDark ? '#93c5fd' : '#0369a1' }}>
            Integration Service
          </p>
          <Tag color="geekblue" className="text-xs">{integration_service}</Tag>
        </div>
      )}
    </div>
  );
};
